import { Component, Input, OnChanges, ViewChild } from '@angular/core';
import { forkJoin } from 'rxjs';
import { take } from 'rxjs/operators';
import { ModalService } from 'src/app/core/services/modal.service';
import { InspectionRecord, InspectionType, VehicleInspection, describeInterval, todayIso, ukDate } from '../../models/inspection.models';
import { VehicleInspectionService } from '../../services/vehicle-inspection.service';
import { InspectionActionModalComponent, InspectionTarget } from '../../modals/inspection-action-modal/inspection-action-modal.component';

/** The Inspections tab on the vehicle screen: what this vehicle needs, when, and its history. */
@Component({
    selector: 'app-vehicle-inspections-tab',
    templateUrl: './vehicle-inspections-tab.component.html',
    standalone: false
})
export class VehicleInspectionsTabComponent implements OnChanges {
  @Input() vehicleId: number = -1;
  @Input() registration: string = '';
  @ViewChild(InspectionActionModalComponent) modal: InspectionActionModalComponent;

  inspections: VehicleInspection[] = [];
  history: InspectionRecord[] = [];
  types: InspectionType[] = [];
  newTypeId: number = -1;
  newDue: string = '';
  error: string = '';
  target: InspectionTarget | null = null;
  mode: 'complete' | 'due' = 'complete';

  readonly ukDate = ukDate;
  readonly describeInterval = describeInterval;
  readonly today = todayIso();

  constructor(private service: VehicleInspectionService, private modalService: ModalService) { }

  ngOnChanges(): void {
    if (this.vehicleId > 0) {
      this.load();
    }
  }

  load(): void {
    forkJoin([this.service.forVehicle(this.vehicleId), this.service.history(this.vehicleId), this.service.getTypes(true)])
      .pipe(take(1)).subscribe({
        next: ([inspections, history, types]) => {
          this.inspections = inspections;
          this.history = history;
          this.types = types;
        },
        error: () => this.error = 'Inspections could not be loaded.',
      });
  }

  /** Active types this vehicle doesn't have yet. */
  availableTypes(): InspectionType[] {
    const has = new Set(this.inspections.map((i) => i.inspectionTypeId));
    return this.types.filter((t) => !has.has(t.id));
  }

  add(): void {
    this.error = '';
    if (!(this.newTypeId > 0) || !this.newDue) {
      this.error = 'Choose an inspection type and its next due date.';
      return;
    }
    this.service.add(this.vehicleId, Number(this.newTypeId), this.newDue).pipe(take(1)).subscribe({
      next: () => {
        this.newTypeId = -1;
        this.newDue = '';
        this.load();
      },
      error: (e) => this.error = (e && e.error && e.error.message) || 'The inspection could not be added.',
    });
  }

  open(i: VehicleInspection, mode: 'complete' | 'due'): void {
    this.mode = mode;
    this.target = { id: i.id, registration: this.registration, typeName: i.inspectionType?.name || '', nextDueDate: i.nextDueDate, lastDoneDate: i.lastDoneDate };
    setTimeout(() => {
      this.modal.reset();
      this.modalService.open('vehicleInspectionAction');
    });
  }

  remove(i: VehicleInspection): void {
    if (!confirm(`Remove ${i.inspectionType?.name || 'this inspection'} from ${this.registration}? Its history is kept.`)) {
      return;
    }
    this.service.remove(i.id).pipe(take(1)).subscribe({
      next: () => this.load(),
      error: () => this.error = 'The inspection could not be removed.',
    });
  }
}
